import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SeoJsonLd from '../../components/feature/SeoJsonLd';
import { supabase } from '../../lib/supabase';

const FORM_URL = 'https://readdy.ai/api/form/d6ic0mcbcbcsu13rgg30';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const jobPostingSchema = {
  '@context': 'https://schema.org',
  '@type': 'JobPosting',
  title: 'Livreur indépendant – Localygo',
  description: 'Rejoignez Localygo en tant que livreur indépendant. Horaires flexibles, assurance incluse, support 7j/7. Livrez dans votre quartier et gagnez à votre rythme.',
  hiringOrganization: {
    '@type': 'Organization',
    name: 'Localygo',
    sameAs: SITE_URL,
  },
  jobLocationType: 'TELECOMMUTE',
  employmentType: 'CONTRACTOR',
  datePosted: '2025-07-14',
  validThrough: '2025-12-31',
  applicantLocationRequirements: {
    '@type': 'Country',
    name: 'France',
  },
};

const avantages = [
  {
    icon: 'ri-time-line',
    title: 'Horaires flexibles',
    desc: 'Travaillez quand vous voulez, selon votre emploi du temps. Matin, soir, week-end — c\'est vous qui décidez.',
  },
  {
    icon: 'ri-map-pin-line',
    title: 'Livraison locale',
    desc: 'Travaillez dans votre quartier avec Localygo. Pas de longs trajets, tout est proche.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Assurance incluse',
    desc: 'Vous êtes couvert pendant toutes vos livraisons. Votre sécurité est notre priorité.',
  },
  {
    icon: 'ri-smartphone-line',
    title: 'App intuitive',
    desc: 'Une application simple et efficace pour gérer vos courses, votre planning et vos gains en temps réel.',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Support 7j/7',
    desc: 'Notre équipe est disponible tous les jours pour vous accompagner et répondre à vos questions.',
  },
];

const etapes = [
  {
    num: '01',
    title: 'Remplissez le formulaire',
    desc: 'Complétez votre candidature en ligne en quelques minutes. Renseignez vos informations et votre moyen de transport.',
  },
  {
    num: '02',
    title: 'Validation de votre profil',
    desc: 'Notre équipe examine votre dossier sous 48h. Vous recevez une confirmation par email ou téléphone.',
  },
  {
    num: '03',
    title: 'Formation rapide',
    desc: 'Participez à une courte session d\'intégration pour maîtriser l\'application et les bonnes pratiques.',
  },
  {
    num: '04',
    title: 'Commencez à livrer !',
    desc: 'Activez votre compte, acceptez vos premières courses et commencez à gagner de l\'argent dès le premier jour.',
  },
];

export default function DevenirLivreurPage() {
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    ville: '',
    transport: '',
    disponibilite: '',
    experience: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    if (!formData.prenom || !formData.nom || !formData.email || !formData.telephone || !formData.transport) {
      setError('Veuillez remplir tous les champs obligatoires.');
      return;
    }
    setLoading(true);
    try {
      const { error: insertError } = await supabase
        .from('candidatures_livreurs')
        .insert([{
          prenom: formData.prenom,
          nom: formData.nom,
          email: formData.email,
          telephone: formData.telephone,
          ville: formData.ville,
          transport: formData.transport,
          disponibilite: formData.disponibilite,
          experience: formData.experience,
          message: formData.message,
        }]);

      if (insertError) throw insertError;
      
      setSubmitted(true);
    } catch (err) {
      console.error('Erreur lors de l\'envoi:', err);
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoJsonLd data={jobPostingSchema} id="schema-livreur" />
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://public.readdy.ai/ai/img_res/3d146631f251b1906f47152bb22cbc9c.jpg"
            alt="Devenir livreur Localy"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-2xl">
            <span className="inline-block bg-teal-500/20 border border-teal-400/40 text-teal-300 text-xs font-semibold px-4 py-1.5 rounded-full mb-6 uppercase tracking-widest">
              Rejoignez l'équipe Localy
            </span>
            <h1 className="text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Devenez livreur<br />
              <span className="text-teal-400">avec Localygo</span>
            </h1>
            <p className="text-lg text-white/80 mb-10 leading-relaxed">
              Travaillez à votre rythme, gagnez bien et contribuez à l'économie locale. Rejoignez plus de 200 livreurs qui font confiance à Localy.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#candidature"
                className="bg-teal-500 hover:bg-teal-400 text-white px-8 py-4 rounded-full font-semibold text-base transition-all shadow-lg hover:shadow-teal-500/30 cursor-pointer whitespace-nowrap"
              >
                Postuler maintenant
              </a>
              <a
                href="#avantages"
                className="border border-white/50 text-white hover:bg-white/10 px-8 py-4 rounded-full font-semibold text-base transition-all cursor-pointer whitespace-nowrap"
              >
                En savoir plus
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-teal-600 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 text-center text-white">
            {[
              { value: '200+', label: 'Livreurs actifs' },
              { value: '48h', label: 'Délai de validation' },
              { value: '7j/7', label: 'Support disponible' },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-3xl font-extrabold mb-1">{stat.value}</div>
                <div className="text-sm text-teal-100">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Avantages */}
      <section id="avantages" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">Pourquoi livrer avec Localy ?</h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Des conditions pensées pour vous permettre de travailler sereinement et de gagner votre vie à votre façon.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {avantages.map((a) => (
              <div key={a.title} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-all group">
                <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-xl mb-5 group-hover:bg-teal-600 transition-colors">
                  <i className={`${a.icon} text-2xl text-teal-600 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{a.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">Comment ça marche ?</h2>
            <p className="text-gray-500 text-lg max-w-xl mx-auto">
              Rejoindre Localy est simple et rapide. Voici les 4 étapes pour commencer à livrer.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
            {etapes.map((e, i) => (
              <div key={e.num} className="relative">
                {i < etapes.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-teal-200 z-0" style={{ width: 'calc(100% - 2rem)', left: 'calc(50% + 2rem)' }}></div>
                )}
                <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-all text-center relative z-10">
                  <div className="w-16 h-16 flex items-center justify-center bg-teal-600 text-white rounded-full text-xl font-extrabold mx-auto mb-5">
                    {e.num}
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-3">{e.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{e.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Formulaire de candidature */}
      <section id="candidature" className="py-24 bg-gradient-to-br from-teal-50 to-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">Postulez dès maintenant</h2>
            <p className="text-gray-500 text-lg">
              Remplissez ce formulaire et notre équipe vous contactera sous 48h.
            </p>
          </div>

          {submitted ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-6">
                <i className="ri-check-line text-4xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Candidature envoyée !</h3>
              <p className="text-gray-500 text-base mb-6">
                Merci <strong>{formData.prenom}</strong> ! Nous avons bien reçu votre candidature. Notre équipe vous contactera dans les 48h.
              </p>
              <button
                onClick={() => { setSubmitted(false); setFormData({ prenom: '', nom: '', email: '', telephone: '', ville: '', transport: '', disponibilite: '', experience: '', message: '' }); }}
                className="bg-teal-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Nouvelle candidature
              </button>
            </div>
          ) : (
            <form
              data-readdy-form
              id="candidature-livreur"
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl shadow-lg p-10 space-y-6"
            >
              {/* Nom / Prénom */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Prénom <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleChange}
                    placeholder="Votre prénom"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nom <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    placeholder="Votre nom"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              {/* Email / Téléphone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email <span className="text-red-500">*</span></label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="votre@email.fr"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Téléphone <span className="text-red-500">*</span></label>
                  <input
                    type="tel"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleChange}
                    placeholder="06 XX XX XX XX"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              {/* Ville */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Ville de résidence</label>
                <input
                  type="text"
                  name="ville"
                  value={formData.ville}
                  onChange={handleChange}
                  placeholder="Ex : Votre ville..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              {/* Moyen de transport */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Moyen de transport <span className="text-red-500">*</span></label>
                <select
                  name="transport"
                  value={formData.transport}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
                  required
                >
                  <option value="">Sélectionnez votre moyen de transport</option>
                  <option value="Vélo">Vélo</option>
                  <option value="Vélo électrique">Vélo électrique</option>
                  <option value="Scooter / Moto">Scooter / Moto</option>
                  <option value="Voiture">Voiture</option>
                  <option value="À pied">À pied</option>
                </select>
              </div>

              {/* Disponibilité */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Disponibilité</label>
                <select
                  name="disponibilite"
                  value={formData.disponibilite}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
                >
                  <option value="">Sélectionnez vos disponibilités</option>
                  <option value="Temps plein">Temps plein</option>
                  <option value="Temps partiel - matin">Temps partiel – matin</option>
                  <option value="Temps partiel - soir">Temps partiel – soir</option>
                  <option value="Week-end uniquement">Week-end uniquement</option>
                  <option value="Flexible">Flexible</option>
                </select>
              </div>

              {/* Expérience */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Expérience en livraison</label>
                <select
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
                >
                  <option value="">Sélectionnez votre expérience</option>
                  <option value="Aucune expérience">Aucune expérience</option>
                  <option value="Moins de 6 mois">Moins de 6 mois</option>
                  <option value="6 mois à 1 an">6 mois à 1 an</option>
                  <option value="Plus d'1 an">Plus d'1 an</option>
                </select>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Message (optionnel)</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Parlez-nous de vous, de vos motivations..."
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                />
                <div className="text-right text-xs text-gray-400 mt-1">{formData.message.length}/500</div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white py-4 rounded-full font-semibold text-base transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Envoi en cours...
                  </span>
                ) : (
                  'Envoyer ma candidature'
                )}
              </button>

              <p className="text-center text-xs text-gray-400">
                En soumettant ce formulaire, vous acceptez d'être contacté par l'équipe Localy.
              </p>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
